import type { CurrentUser } from "@/lib/api/types";

export type { CurrentUser };

/**
 * Platform roles (User.role on the backend). Distinct from Django's
 * is_superuser and from per-group roles. See docs/roles-and-permissions.md.
 */
export const PlatformRole = {
  Admin: "admin",
  SuperUser: "super_user",
  NoRole: "",
} as const;

/**
 * Is `user` an Admin by platform role? For *labels only* — never for gating.
 * Access decisions go through `canManageBackOffice`, which also covers Django
 * superusers, as every admin-only endpoint does.
 */
export function isAdmin(user: CurrentUser | null | undefined): boolean {
  return user?.role === PlatformRole.Admin;
}

export function isSuperUser(user: CurrentUser | null | undefined): boolean {
  return user?.role === PlatformRole.SuperUser;
}

/**
 * Capability checks read the backend-computed `permissions` block (the
 * authoritative source — the API resolves platform role + group role + Django
 * is_superuser into these booleans). The SPA must never re-derive access from
 * raw role strings; it mirrors what the server already decided.
 */
export function canAccessReports(user: CurrentUser | null | undefined): boolean {
  return user?.permissions.can_access_reports ?? false;
}

export function canManagePartnerGroups(user: CurrentUser | null | undefined): boolean {
  return user?.permissions.can_manage_partner_groups ?? false;
}

/** May review & publish enterprise communications (the `validator` role). */
export function canReviewCommunications(
  user: CurrentUser | null | undefined,
): boolean {
  return user?.permissions.can_review_communications ?? false;
}

/** Is `user` an expert (self-registration wizard + dashboard access)? */
export function isExpert(user: CurrentUser | null | undefined): boolean {
  return user?.permissions.is_expert ?? false;
}

/**
 * May `user` reach the CQDD back-office (Admin role or Django superuser)?
 *
 * The gate for admin-only screens, mirroring the API's IsAdminUser. Gating on
 * `isAdmin` instead left those screens unreachable for Django superusers —
 * accounts that do reach their Django equivalents, since AdminRequiredMixin
 * allows them.
 */
export function canManageBackOffice(user: CurrentUser | null | undefined): boolean {
  return user?.permissions.is_admin ?? false;
}

/** May `user` manage the CQDD expert back-office (admin/Django superuser)? */
export function canManageExperts(user: CurrentUser | null | undefined): boolean {
  return user?.permissions.is_expert_admin ?? false;
}

export function isPartner(user: CurrentUser | null | undefined): boolean {
  return user?.permissions.is_partner ?? false;
}

export function isGroupMember(user: CurrentUser | null | undefined): boolean {
  return user?.permissions.is_group_member ?? false;
}

/**
 * A LocalPartner who is a Partner in no group. They can't create groups, so the
 * partner area opens their single group directly (see PartnersApp) instead of a
 * one-item list — mirrors the old PartnerGroupListView.get redirect.
 */
export function isLocalPartnerOnly(user: CurrentUser | null | undefined): boolean {
  return user?.permissions.is_local_partner_only ?? false;
}

/**
 * Should the "Groupe partenaire" tab be visible? Server-decided flag, oracle
 * `home_view.py`: is_super_user() || is_partner(). A plain business admin does
 * NOT see it (B-3) — do NOT fold in canManagePartnerGroups (that includes admins).
 */
export function showPartnerTab(user: CurrentUser | null | undefined): boolean {
  return user?.permissions.show_partner_tab ?? false;
}

/**
 * A partner (or régional partner / super-user) who may not reach the
 * Activateur — so, since OAC-462, nobody: a partner without a licence now gets
 * the whole application, and the server says so through
 * `has_activateur_access`.
 *
 * The predicate is kept (renaming it is out of that ticket's scope) but it
 * reads the access flag rather than `has_active_subscription`, which still
 * means "genuinely holds a licence or pays" and is what billing reads. The
 * stripped header and group landing it selects therefore serve no one today;
 * that is the intended effect, not an accident — the demo-partner accounts of
 * OAC-469 are the next candidates to key off it.
 */
export function isPartnerWithoutLicense(
  user: CurrentUser | null | undefined,
): boolean {
  return showPartnerTab(user) && !(user?.has_activateur_access ?? false);
}

/**
 * Should the "Billing & subscription" entry appear in the user menu? Billing
 * manages a personal enterprise subscription, so it's hidden from admins,
 * régional partners (super_user), experts, and partners/local-partners — who
 * don't hold a personal subscription. Mirrors the legacy user-menu gating.
 */
export function showBilling(user: CurrentUser | null | undefined): boolean {
  if (!user) return false;
  // Admins / régional partners / Django superusers manage groups, don't
  // subscribe personally (canManagePartnerGroups folds all three in); partners
  // and local partners subscribe via a licence; experts aren't enterprises.
  return (
    !canManagePartnerGroups(user) && !isPartner(user) && user.role !== "expert"
  );
}

/**
 * The role-appropriate home/landing route:
 * - experts land on (and are restricted to) their own dashboard,
 * - a partner / super-user WITHOUT a member licence lands on the group area
 *   (`PartnersApp` opens the single group's detail for a local-partner-only),
 * - everyone else (members, licensed partners, admins) lands on the enterprise
 *   dashboard.
 */
export function landingPath(user: CurrentUser | null | undefined): string {
  if (isExpert(user)) return "/experts/dashboard";
  if (isPartnerWithoutLicense(user)) return "/partner-group";
  return "/dashboard";
}

/**
 * Named capabilities a route may require. `authenticated` only needs a valid
 * session; the others map to the permission booleans above.
 */
export type Capability =
  | "authenticated"
  | "reports"
  | "partnerGroups"
  | "communications"
  | "expert"
  | "expertAdmin"
  | "backOffice";

const CAPABILITY_CHECKS: Record<
  Capability,
  (user: CurrentUser | null | undefined) => boolean
> = {
  authenticated: (user) => user != null,
  reports: canAccessReports,
  partnerGroups: (user) => canManagePartnerGroups(user) || showPartnerTab(user),
  communications: canReviewCommunications,
  expert: isExpert,
  expertAdmin: canManageExperts,
  backOffice: canManageBackOffice,
};

/** Does `user` satisfy every required capability? */
export function hasCapabilities(
  user: CurrentUser | null | undefined,
  required: readonly Capability[],
): boolean {
  return required.every((cap) => CAPABILITY_CHECKS[cap](user));
}
